import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { authApi } from "../../../lib/api-client";
import { isBrowser } from "../../../lib/utils";

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
	const [user, setUser] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const navigate = useNavigate();
	const location = useLocation();

	const pathname = location.pathname;
	const isAuthenticated = !!user;

	useEffect(() => {
		const checkAuth = async () => {
			if (!isBrowser) {
				setIsLoading(false);
				return;
			}

			const token = Cookies.get("ighota_auth_token");

			if (token) {
				try {
					const userData = await authApi.getCurrentUser();
					setUser(userData);

					if (
						userData &&
						userData.emailVerified === false &&
						!pathname.includes("/auth/verify-email") &&
						!pathname.includes("/auth/logout")
					) {
						navigate("/auth/verify-email");
					}
				} catch (error) {
					console.error("Failed to fetch user data:", error);
					authApi.logout();
				}
			}

			setIsLoading(false);
		};

		checkAuth();
	}, [pathname]);

	const login = async (email, password) => {
		try {
			setIsLoading(true);
			const response = await authApi.login(email, password);
			setUser(response.user);

			if (response.user.emailVerified === false) {
				navigate("/auth/verify-email");
			} else {
				navigate("/dashboard");
			}
		} catch (error) {
			console.error("Login failed:", error);
			throw error;
		} finally {
			setIsLoading(false);
		}
	};

	const register = async (userData) => {
		try {
			setIsLoading(true);
			const response = await authApi.register(userData);
			setUser(response.user);
			navigate("/auth/verify-email");
		} catch (error) {
			console.error("Registration failed:", error);
			throw error;
		} finally {
			setIsLoading(false);
		}
	};

	const logout = () => {
		authApi.logout();
		setUser(null);
		navigate("/");
	};

	const signOut = logout;

	const value = {
		user,
		isLoading,
		isAuthenticated,
		login,
		register,
		logout,
		signOut,
	};

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
